import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useNavigation } from '../hooks/useNavigation';
import { pathWithEmpresa } from '../utils/companyUrl';

const Sidebar = ({ user, company, onLogout, onChangeCompany }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { filteredNavItems: navItems, isSupervisor, isSsoma } = useNavigation();

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    if (window.confirm('¿Estás seguro de que deseas cerrar sesión?')) {
      onLogout();
    }
  };

  const handleChangeCompany = () => {
    if (window.confirm('¿Deseas cambiar de empresa? Esto te llevará al selector de empresas.')) {
      onChangeCompany?.();
    }
  };

  const handleMobileMenuClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-gray-200"
      >
        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`bg-white shadow-lg h-screen w-64 fixed left-0 top-0 z-40 border-r border-gray-200 flex flex-col transform transition-transform duration-300 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        
        {/* Mobile Close Button */}
        <button
          onClick={() => setIsMobileMenuOpen(false)}
          className="lg:hidden absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Logo y empresa */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3 mb-4">
            <div className={`w-12 h-12 bg-gradient-to-br ${company?.color || 'from-teal-500 to-teal-600'} rounded-full flex items-center justify-center`}>
              <span className="text-white text-xl font-bold">{company?.logo || 'P'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-900">Sistema de Planillas</h1>
            </div>
          </div>
          
          {/* Información de la empresa */}
          {company && (
            <div className="bg-gray-50 rounded-lg p-3">
              <h2 className="text-sm font-semibold text-gray-900 truncate">{company.name}</h2>
              <p className="text-xs text-gray-500">RUC: {company.ruc}</p>
              <p className="text-xs text-gray-500">{company.employees} empleados</p>
              {!user?.empresa_id && (
                <button
                  onClick={handleChangeCompany}
                  className="mt-2 text-xs text-teal-600 hover:text-teal-700 font-medium"
                >
                  Cambiar empresa
                </button>
              )}
            </div>
          )}
        </div>

        {/* Navegación con scroll */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <nav className="mt-4 px-4 flex-1 overflow-y-auto">
            <ul className="space-y-2 pb-4">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={pathWithEmpresa(item.path, company?.id)}
                    onClick={handleMobileMenuClick}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      isActive(item.path)
                        ? 'bg-teal-50 text-teal-700 border-r-4 border-teal-500 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <span className={isActive(item.path) ? 'text-teal-600' : 'text-gray-400'}>
                      {item.icon}
                    </span>
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Usuario y logout */}
          <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-teal-600 font-bold text-sm">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">
                  {isSsoma ? 'SSOMA' : isSupervisor ? 'Supervisor' : (user?.role || 'Usuario')}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 